import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconArrowLeft = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const IconCopy = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const IconNav = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 11 22 2 13 21 11 13 3 11"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function safeStringify(val) {
  if (val === null) return 'null';
  if (val === undefined) return 'undefined';
  if (typeof val === 'function') return '[Function]';
  if (typeof val === 'object') {
    try { return JSON.stringify(val, null, 2); } catch { return String(val); }
  }
  return String(val);
}

function collectTgData() {
  const tg = window.Telegram?.WebApp;
  if (!tg) return null;

  const sections = [];

  // ── Core ──
  sections.push({
    title: 'Core',
    rows: [
      ['version', tg.version],
      ['platform', tg.platform],
      ['colorScheme', tg.colorScheme],
      ['isExpanded', tg.isExpanded],
      ['viewportHeight', tg.viewportHeight],
      ['viewportStableHeight', tg.viewportStableHeight],
      ['headerColor', tg.headerColor],
      ['backgroundColor', tg.backgroundColor],
      ['isClosingConfirmationEnabled', tg.isClosingConfirmationEnabled],
    ],
  });

  // ── initData ──
  sections.push({
    title: 'initData (raw)',
    rows: [['initData', tg.initData || '(пусто)']],
  });

  // ── initDataUnsafe ──
  const unsafe = tg.initDataUnsafe || {};
  const unsafeRows = Object.entries(unsafe).map(([k, v]) => [k, safeStringify(v)]);
  sections.push({
    title: 'initDataUnsafe',
    rows: unsafeRows.length ? unsafeRows : [['(пусто)', '']],
  });

  // ── User ──
  const user = unsafe.user || {};
  sections.push({
    title: 'User',
    rows: Object.keys(user).length
      ? Object.entries(user).map(([k, v]) => [k, safeStringify(v)])
      : [['(нет пользователя)', '']],
  });

  // ── themeParams ──
  const theme = tg.themeParams || {};
  sections.push({
    title: 'themeParams',
    rows: Object.keys(theme).length
      ? Object.entries(theme).map(([k, v]) => [k, safeStringify(v)])
      : [['(пусто)', '']],
  });

  // ── MainButton ──
  const mb = tg.MainButton;
  sections.push({
    title: 'MainButton',
    rows: mb ? [
      ['text', mb.text],
      ['color', mb.color],
      ['textColor', mb.textColor],
      ['isVisible', mb.isVisible],
      ['isActive', mb.isActive],
      ['isProgressVisible', mb.isProgressVisible],
    ] : [['(недоступно)', '']],
  });

  // ── BackButton ──
  const bb = tg.BackButton;
  sections.push({
    title: 'BackButton',
    rows: bb ? [['isVisible', bb.isVisible]] : [['(недоступно)', '']],
  });

  // ── HapticFeedback ──
  sections.push({
    title: 'HapticFeedback',
    rows: [['доступен', tg.HapticFeedback ? 'да' : 'нет']],
  });

  // ── CloudStorage ──
  sections.push({
    title: 'CloudStorage',
    rows: [['доступен', tg.CloudStorage ? 'да' : 'нет']],
  });

  // ── BiometricManager ──
  sections.push({
    title: 'BiometricManager',
    rows: [['доступен', tg.BiometricManager ? 'да' : 'нет']],
  });

  return sections;
}

// ─── Navigate panel ───────────────────────────────────────────────────────────

const NavPanel = () => {
  const [url, setUrl] = useState('');
  const navigate = useNavigate();

  const go = () => {
    if (!url.trim()) return;
    const trimmed = url.trim();
    // Если введён полный URL — используем location, если путь — navigate
    if (trimmed.startsWith('http')) {
      window.location.href = trimmed;
    } else {
      navigate(trimmed.startsWith('/') ? trimmed : '/' + trimmed);
    }
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      border: '1.5px solid #e0e7ff',
      padding: '14px 14px',
      marginBottom: 8,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>
        🧭 Навигация
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && go()}
          placeholder="/events/123  или  https://..."
          style={{
            flex: 1,
            fontSize: 13,
            padding: '8px 12px',
            borderRadius: 9,
            border: '1.5px solid #e0e7ff',
            outline: 'none',
            fontFamily: 'monospace',
            color: '#1e1b4b',
          }}
        />
        <button
          onClick={go}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            color: '#fff', border: 'none', borderRadius: 9,
            padding: '8px 14px', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', flexShrink: 0,
          }}
        >
          <IconNav /> Перейти
        </button>
      </div>
    </div>
  );
};

// ─── Section ──────────────────────────────────────────────────────────────────

const Section = ({ title, rows }) => {
  const [copied, setCopied] = useState(null);

  const copy = (val, key) => {
    navigator.clipboard?.writeText(String(val)).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      border: '1.5px solid #f0f0f5',
      overflow: 'hidden',
      marginBottom: 8,
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        padding: '7px 14px',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {title}
        </span>
      </div>
      <div>
        {rows.map(([key, val], i) => (
          <div
            key={key + i}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: '8px 14px',
              borderTop: i > 0 ? '1px solid #f5f5fa' : 'none',
              gap: 10,
            }}
          >
            <span style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#6b7280',
              minWidth: 130,
              flexShrink: 0,
              paddingTop: 1,
              fontFamily: 'monospace',
            }}>
              {key}
            </span>
            <span style={{
              fontSize: 12,
              color: '#1e1b4b',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              flex: 1,
              whiteSpace: 'pre-wrap',
            }}>
              {safeStringify(val)}
            </span>
            {val !== '' && val !== undefined && (
              <button
                onClick={() => copy(val, key + i)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: copied === key + i ? '#22c55e' : '#9ca3af',
                  padding: '2px 4px', flexShrink: 0,
                  transition: 'color 0.2s',
                }}
                title="Скопировать"
              >
                <IconCopy />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const DebugPage = () => {
  const navigate = useNavigate();
  const tg = window.Telegram?.WebApp;
  const sections = collectTgData();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f8fafc' }}>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        padding: '14px 16px 16px',
        color: '#fff',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'rgba(255,255,255,0.18)', border: 'none',
              borderRadius: 9, width: 34, height: 34,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff', flexShrink: 0,
            }}
          >
            <IconArrowLeft />
          </button>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: -0.3 }}>
              Debug
            </h1>
            <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>
              Telegram WebApp данные
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 24px' }}>

        {/* Nav panel */}
        <NavPanel />

        {/* TG not available */}
        {!tg && (
          <div style={{
            background: '#fef2f2', border: '1.5px solid #fca5a5',
            borderRadius: 14, padding: '14px 16px', marginBottom: 8,
          }}>
            <span style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>
              ⚠️ window.Telegram.WebApp недоступен — открыто не в Telegram
            </span>
          </div>
        )}

        {/* Browser info (always) */}
        <Section title="Browser / Window" rows={[
          ['userAgent', navigator.userAgent],
          ['language', navigator.language],
          ['platform', navigator.platform],
          ['window.innerWidth', window.innerWidth],
          ['window.innerHeight', window.innerHeight],
          ['location.href', window.location.href],
          ['location.pathname', window.location.pathname],
        ]} />

        {/* TG sections */}
        {sections && sections.map(s => (
          <Section key={s.title} title={s.title} rows={s.rows} />
        ))}
      </div>
    </div>
  );
};

export default DebugPage;